import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { ShippingCompany, ShippingCompanyFormData } from "@/types/shipping-company";

interface EditShippingCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: ShippingCompany;
  onSuccess: () => void;
}

export const EditShippingCompanyDialog = ({ open, onOpenChange, company, onSuccess }: EditShippingCompanyDialogProps) => {
  const [formData, setFormData] = useState<ShippingCompanyFormData>({
    name_ar: "",
    name_en: "",
    code: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        name_ar: company.name_ar,
        name_en: company.name_en,
        code: company.code,
      });
    }
  }, [company]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from("shipping_companies")
        .update(formData)
        .eq("id", company.id);

      if (error) throw error;

      toast.success("تم تحديث شركة النقل البحري بنجاح");
      onSuccess();
    } catch (error) {
      console.error("Error updating shipping company:", error);
      toast.error("حدث خطأ أثناء تحديث شركة النقل البحري");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("هل أنت متأكد من حذف هذه الشركة؟")) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("shipping_companies")
        .delete()
        .eq("id", company.id);

      if (error) throw error;

      toast.success("تم حذف شركة النقل البحري بنجاح");
      onSuccess();
    } catch (error) {
      console.error("Error deleting shipping company:", error);
      toast.error("حدث خطأ أثناء حذف شركة النقل البحري");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" dir="rtl">
        <DialogHeader>
          <DialogTitle>تعديل شركة النقل البحري</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name_ar">الاسم بالعربية *</Label>
            <Input
              id="name_ar"
              value={formData.name_ar}
              onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
              placeholder="أدخل اسم الشركة بالعربية"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name_en">الاسم بالإنجليزية *</Label>
            <Input
              id="name_en"
              value={formData.name_en}
              onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
              placeholder="أدخل اسم الشركة بالإنجليزية"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">كود الشركة *</Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="أدخل كود الشركة (مثل: MSC)"
              required
              maxLength={10}
            />
          </div>

          <div className="flex justify-between">
            <Button type="button" variant="destructive" onClick={handleDelete} disabled={isLoading}>
              حذف الشركة
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                إلغاء
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "جاري التحديث..." : "تحديث الشركة"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};